import { Link } from 'react-router-dom';
import { Mail, Phone, ArrowUpRight, MessageCircle, Sparkles } from 'lucide-react';
import { useSite } from '../lib/SiteContentContext';
import { BOOK_TRIAL_URL } from '../lib/config';

const exploreLinks = [
  { to: '/courses', label: 'Courses' },
  { to: '/services', label: 'How we teach' },
  { to: '/about', label: 'About us' },
  { to: '/contact', label: 'Contact' },
];

const supportLinks = [
  { to: '/#faq-heading', label: 'FAQs', external: false },
  { to: '/privacy', label: 'Privacy Policy', external: false },
  { to: '/terms', label: 'Terms & Conditions', external: false },
  { to: '/safeguarding', label: 'Safeguarding', external: false },
  { to: '/refunds', label: 'Cancellation policy', external: false },
];

const accreditationBadges = [
  {
    src: '/badges/cambridge-ocr.webp',
    alt: 'Cambridge OCR',
  },
  {
    src: '/badges/aqa-approved-examiner.webp',
    alt: 'AQA Approved Examiner 2025',
  },
  {
    src: '/badges/chartered-college.webp',
    alt: 'Chartered College of Teaching',
  },
  {
    src: '/badges/tutors-association.webp',
    alt: "The Tutors' Association",
  },
];

export default function Footer() {
  const { brand, CTA } = useSite();
  return (
    <footer className="bg-brand-ink text-brand-cream mt-12 md:mt-16">
      <div className="container py-12 md:py-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {/* Explore */}
        <div>
          <div className="text-brand-sun font-semibold text-sm uppercase tracking-widest mb-4">Explore</div>
          <ul className="space-y-2">
            {exploreLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Support */}
        <div>
          <div className="text-brand-sun font-semibold text-sm uppercase tracking-widest mb-4">Support</div>
          <ul className="space-y-2">
            {supportLinks.map((l) => (
              <li key={l.to}>
                {l.external ? (
                  <a
                    href={l.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline"
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link to={l.to} className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline">
                    {l.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <div className="text-brand-sun font-semibold text-sm uppercase tracking-widest mb-4">Contact</div>
          <ul className="space-y-3 text-brand-cream/80">
            <li className="flex items-start gap-2">
              <Mail className="w-4 h-4 mt-1 shrink-0" aria-hidden="true" />
              <a
                href={`mailto:${brand.email}`}
                className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline break-all"
              >
                {brand.email}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Phone className="w-4 h-4 shrink-0" aria-hidden="true" />
              <a
                href={`tel:${brand.phone.replace(/\s/g, '')}`}
                className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline"
              >
                {brand.phoneDisplay}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 shrink-0" aria-hidden="true" />
              <a
                href={brand.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-brand-sun focus-visible:outline-none focus-visible:underline"
              >
                WhatsApp us
              </a>
            </li>
          </ul>
        </div>

        {/* Book a free trial */}
        <div>
          <div className="text-brand-sun font-semibold text-sm uppercase tracking-widest mb-4">
            Book a free trial
          </div>
          <p className="text-brand-cream/80 mb-5">
            One free hour with a qualified UK teacher. No obligation to continue.
          </p>
          <a
            href={BOOK_TRIAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-brand-sun text-brand-ink hover:bg-white rounded-full px-5 py-2.5 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-sun focus-visible:ring-offset-2 focus-visible:ring-offset-brand-ink"
          >
            <Sparkles className="w-4 h-4" aria-hidden="true" />
            {CTA.primary}
            <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>

      {/* Accreditation badges row */}
      <div className="border-t border-brand-cream/10">
        <div className="container py-8">
          <div
            className="flex flex-wrap items-center justify-around gap-x-10 gap-y-6"
            data-testid="footer-accreditation-badges"
          >
            {accreditationBadges.map((b) => (
              <div
                key={b.src}
                className="bg-white rounded-2xl shadow-sm px-5 py-3 flex items-center justify-center"
              >
                <img
                  src={b.src}
                  alt={b.alt}
                  loading="lazy"
                  className="h-16 md:h-20 w-auto object-contain"
                  data-testid={`footer-badge-${b.alt.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer + copyright */}
      <div className="border-t border-brand-cream/10">
        <div className="container py-6 flex flex-col md:flex-row justify-between gap-4 text-xs text-brand-cream/60">
          <p className="max-w-3xl">
            Science Sorted is an independent tuition provider and is not affiliated with or endorsed by AQA or OCR.
          </p>
          <div className="whitespace-nowrap">
            © {new Date().getFullYear()} Science Sorted Ltd. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
}
